import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { S, A, DARK, labelStyle } from './shellTokens';

export type Device = 'mobile' | 'tablet';

export const DEVICES: Record<Device, { label: string; width: number; height: number; window: string }> = {
  mobile: { label: 'Mobile', width: 390, height: 844, window: 'GenesisMobilePreview' },
  tablet: { label: 'Tablet', width: 768, height: 1024, window: 'GenesisTabletPreview' }
};

const COLUMN = 380;
const PAD = 20;

type Props = {
  /** The page as the site renders it, e.g. /us/x?preview=1&v=DRAFT. */
  src: string;
  device: Device;
  onDevice: (device: Device) => void;
  onClose: () => void;
  /** Changes after each save, to load the saved draft again. */
  reloadKey?: string | number | null;
  note: string;
};

const useStyles = createUseStyles({
  column: {
    width: COLUMN,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`,
    fontFamily: S.ui,
    color: S.fg
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 16px', borderBottom: `1px solid ${S.line}` },
  title: { ...labelStyle, color: S.fg, marginRight: 'auto' },
  segment: { display: 'flex', padding: 2, gap: 2, borderRadius: 7, background: S.line },
  segButton: {
    all: 'unset',
    cursor: 'pointer',
    padding: '3px 10px',
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 600,
    color: S.muted,
    '& small': { fontFamily: S.mono, fontWeight: 400, marginLeft: 4 },
    '&:hover, &:focus-visible': { color: S.fg }
  },
  segOn: { background: S.field, color: S.fg, boxShadow: '0 1px 2px rgba(23, 26, 32, 0.15)' },
  icon: {
    all: 'unset',
    cursor: 'pointer',
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    color: S.muted,
    '& .material-icons': { fontSize: 18 },
    '&:hover, &:focus-visible': { background: S.chromeHover, color: S.fg }
  },
  body: { flex: 1, overflowX: 'hidden', overflowY: 'auto', padding: PAD, display: 'grid', justifyContent: 'center', alignContent: 'start', gap: 10 },
  bezel: { position: 'relative', overflow: 'hidden', borderRadius: 14, background: S.field, boxShadow: `0 0 0 6px ${S.fg}, 0 10px 30px rgba(23, 26, 32, 0.25)`, [DARK]: { boxShadow: `0 0 0 6px ${S.fg}, ${A.pop}` } },
  frame: { border: 0, transformOrigin: '0 0', display: 'block', background: S.field },
  loading: { position: 'absolute', top: 10, right: 10, fontSize: 11, fontFamily: S.mono, padding: '2px 6px', borderRadius: 3, background: S.fg, color: S.field },
  note: { fontSize: 12, color: S.muted, textAlign: 'center', maxWidth: COLUMN - PAD * 2 }
});

/**
 * The saved draft as the site renders it, at a real phone or tablet width, so
 * media queries apply. Reloads after each save and keeps the scroll position.
 */
const DeviceFrames: React.FC<Props> = ({ src, device, onDevice, onClose, reloadKey, note }) => {
  const c = useStyles();
  const ref = useRef<HTMLIFrameElement>(null);
  const scrollY = useRef(0);
  const [loading, setLoading] = useState(true);
  const d = DEVICES[device];
  const scale = Math.min(1, (COLUMN - PAD * 2 - 12) / d.width);

  // Reload in place after a save; the load handler puts the scroll back.
  const firstKey = useRef(reloadKey);
  useEffect(() => {
    if (reloadKey === firstKey.current) return;
    firstKey.current = reloadKey;
    const win = ref.current && ref.current.contentWindow;
    if (!win) return;
    try {
      scrollY.current = win.scrollY;
      setLoading(true);
      win.location.reload();
    } catch (e) {
      // Not reachable (another origin); load it again from the top.
      if (ref.current) ref.current.src = src;
    }
  }, [reloadKey, src]);

  const onLoad = () => {
    setLoading(false);
    const win = ref.current && ref.current.contentWindow;
    try {
      if (!win) return;
      // Phones and tablets overlay their scrollbars; without this the page lays out ~15px narrower.
      const style = win.document.createElement('style');
      style.textContent = 'html { scrollbar-width: none; } html::-webkit-scrollbar { display: none; }';
      win.document.head.appendChild(style);
      if (scrollY.current) win.scrollTo(0, scrollY.current);
    } catch (e) { /* not reachable */ }
  };

  return (
    <aside className={c.column} aria-label="Device preview" data-testid="device-frames">
      <div className={c.head}>
        <span className={c.title}>Preview</span>
        <div className={c.segment} role="group" aria-label="Device">
          {(Object.keys(DEVICES) as Device[]).map(key => (
            <button
              key={key}
              type="button"
              className={`${c.segButton} ${key === device ? c.segOn : ''}`}
              aria-pressed={key === device}
              onClick={() => { scrollY.current = 0; setLoading(true); onDevice(key); }}
            >
              {DEVICES[key].label}<small>{DEVICES[key].width}</small>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={c.icon}
          title="Open in a window"
          aria-label="Open in a window"
          onClick={() => window.open(src, d.window, `popup,width=${d.width},height=${d.height}`)}
        >
          <i className="material-icons">open_in_new</i>
        </button>
        <button type="button" className={c.icon} title="Close preview" aria-label="Close preview" onClick={onClose}>
          <i className="material-icons">close</i>
        </button>
      </div>
      <div className={c.body}>
        <div className={c.bezel} style={{ width: Math.round(d.width * scale), height: Math.round(d.height * scale) }}>
          <iframe
            key={device}
            ref={ref}
            title={`${d.label} preview`}
            src={src}
            className={c.frame}
            style={{ width: d.width, height: d.height, transform: `scale(${scale})` }}
            onLoad={onLoad}
          />
          {loading && <span className={c.loading}>loading…</span>}
        </div>
        <div className={c.note} data-testid="device-note">{note}</div>
      </div>
    </aside>
  );
};

export default DeviceFrames;
